import type { OnboardingCalibrationContinuation } from '$lib/apis/content-factory/onboarding';
import type { Onboarding1FlowState } from '$lib/components/content-factory/onboarding/onboarding1Flow';

export const assertOnboardingCalibrationContinuation = (
	value: OnboardingCalibrationContinuation,
	flow: Onboarding1FlowState,
	conversationId: string
): OnboardingCalibrationContinuation => {
	// A fully sourced discovery report can have no clarifying questions. The
	// server still owns its scoped continuation after the user confirms facts.
	if (
		!value ||
		value.schema_version !== 'onboarding_calibration_continuation_v1' ||
		![conversationId, flow.stateId, flow.workspaceId, flow.brandId, flow.discoveryRunId].every(
			(ref) => typeof ref === 'string' && ref.trim()
		) ||
		value.conversation_id !== conversationId ||
		value.state_id !== flow.stateId ||
		value.workspace_id !== flow.workspaceId ||
		value.brand_id !== flow.brandId ||
		value.discovery_run_id !== flow.discoveryRunId ||
		!/^onboarding-calibration-v1:[a-f0-9]{64}$/.test(value.operation_ref) ||
		!['available', 'processing', 'failed', 'unknown', 'completed'].includes(value.status) ||
		!Number.isSafeInteger(value.attempt) ||
		value.attempt < 0 ||
		value.attempt >= Number.MAX_SAFE_INTEGER ||
		(value.status === 'available') !== (value.attempt === 0) ||
		!Array.isArray(value.answered_question_refs) ||
		value.answered_question_refs.some((ref) => typeof ref !== 'string' || !ref.trim()) ||
		new Set(value.answered_question_refs).size !== value.answered_question_refs.length ||
		(value.status === 'completed'
			? !value.voice_run_ref ||
				value.authority?.voice_run_ref !== value.voice_run_ref ||
				value.authority?.discovery_run_ref !== flow.discoveryRunId ||
				value.authority?.schema_version !== 'brand_voice_calibration_authority_v1' ||
				!value.authority?.choice_set_ref ||
				!Array.isArray(value.authority?.choices) ||
				value.authority.choices.length !== 2 ||
				new Set(value.authority.choices.map((choice) => choice.choice_ref)).size !== 2 ||
				value.authority.choices.some(
					(choice) =>
						!choice.choice_ref ||
						typeof choice.example_post !== 'string' ||
						choice.example_post.length < 180 ||
						choice.example_post.length > 720
				)
			: value.voice_run_ref !== null || value.authority !== null)
	) {
		throw new Error('Onboarding calibration continuation authority conflicts');
	}
	return value;
};

export const onboardingCalibrationRecoveryMessage = (
	status: OnboardingCalibrationContinuation['status']
) =>
	status === 'processing' || status === 'unknown'
		? 'Ответ сохранён. Подготовка вариантов стиля ещё не подтверждена. Можно проверить результат — повторно отправлять ответ не нужно.'
		: 'Ответ сохранён. Не удалось закончить подготовку вариантов стиля. Можно продолжить этот этап — повторно отправлять ответ не нужно.';

export class OnboardingCalibrationPendingError extends Error {
	constructor(readonly continuation: OnboardingCalibrationContinuation) {
		super('Onboarding calibration is not completed');
		this.name = 'OnboardingCalibrationPendingError';
	}
}

/** Created only after the answer response passed its exact scoped identity check. */
export class OnboardingAcceptedAnswerContinuationError extends Error {
	constructor(cause: unknown) {
		super('The answer was accepted; its continuation is not confirmed', {
			cause
		});
		this.name = 'OnboardingAcceptedAnswerContinuationError';
	}
}
